import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Download, Loader2 } from "lucide-react";
import { PageHeader } from "@/components/shared/PageHeader";
import { PrimaryButton, SecondaryButton } from "@/components/shared/Buttons";
import { useSeo } from "@/hooks/useSeo";
import { getPaymentStatus, receiptUrl } from "@/lib/paymentApi";

const Row = ({ label, value }) => (
  <div className="flex justify-between gap-4 border-b border-navy/10 py-3 text-sm">
    <span className="text-navy/55">{label}</span>
    <span className="text-right font-medium text-navy">{value || "—"}</span>
  </div>
);

export default function PaymentSuccess() {
  useSeo("Payment Successful", "Your ASAI payment was successful. Download your receipt.");
  const [params] = useSearchParams();
  const ref = params.get("ref");
  const [pay, setPay] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!ref) return setErr("Missing payment reference.");
    getPaymentStatus(ref).then(setPay).catch(() => setErr("Could not load payment details."));
  }, [ref]);

  return (
    <div data-testid="page-payment-success">
      <PageHeader overline="Payment" titleLines={["Payment", "successful"]} />
      <section className="bg-white py-20 md:py-28">
        <div className="container-asai max-w-xl">
          <div className="flex flex-col items-center gap-5 rounded-3xl border border-navy/10 bg-lightgrey p-8 text-center">
            <CheckCircle2 className="h-16 w-16 text-emerald-500" />
            <h2 className="font-heading text-2xl text-navy">Thank you! Your payment was received.</h2>
            {err && <p className="text-sm text-red-500">{err}</p>}
            {!pay && !err && <Loader2 className="h-6 w-6 animate-spin text-orange" />}
            {pay && (
              <>
                <div className="w-full rounded-2xl border border-navy/10 bg-white p-6 text-left" data-testid="payment-success-details">
                  <Row label="Receipt No." value={pay.receiptNumber} />
                  <Row label="Payment Ref" value={pay.paymentRef} />
                  <Row label="Purpose" value={pay.purposeLabel} />
                  <Row label="Amount" value={`₹${pay.amountInr.toLocaleString("en-IN")}`} />
                  <Row label="Status" value={pay.status} />
                  <Row label="Payment ID" value={pay.razorpayPaymentId} />
                </div>
                <a href={receiptUrl(pay.paymentRef)} target="_blank" rel="noopener noreferrer" data-testid="download-receipt"
                  className="inline-flex items-center gap-2 rounded-full bg-orange px-7 py-3.5 text-sm font-medium text-white transition-transform hover:scale-105">
                  <Download className="h-4 w-4" /> Download Receipt
                </a>
              </>
            )}
            <div className="mt-2 flex flex-wrap justify-center gap-3">
              <PrimaryButton to="/student/payment-history" withArrow testId="success-history">View Payment History</PrimaryButton>
              <SecondaryButton to="/" testId="success-home">Back to Home</SecondaryButton>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
